// silks/src/components/OrderFilters.js
// Admin Orders tab additions:
//   <OrderFilterPanel>        — date / status / payment / method / search filters
//   <PaymentAttemptsSection>  — failed, cancelled, pending and paid-without-order
//                               checkouts (they have no Order, so they are shown
//                               separately and are never treated as confirmed)
import React, { useEffect, useState } from "react";
import { RANGES, ORDER_STATUSES, PAY_STATUSES, applyAttemptFilters, attemptMethod, dateRange } from "../utils/orderFilterUtils";

const gold = "#c8a04a", dim = "#7a5a30", text = "#e8d5a3";
const inp = { width: "100%", padding: "8px 10px", fontSize: 12, background: "#0f0a04", border: "1px solid rgba(200,160,74,0.2)", borderRadius: 6, color: text, outline: "none", fontFamily: "'DM Sans',sans-serif" };
const lbl = { display: "block", fontSize: 10, fontWeight: 600, color: dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5, fontFamily: "'DM Sans',sans-serif" };
const pill = (on) => ({ fontSize: 11, padding: "4px 12px", borderRadius: 12, cursor: "pointer", background: on ? "rgba(200,160,74,0.25)" : "rgba(200,160,74,0.07)", border: on ? "1px solid rgba(200,160,74,0.6)" : "1px solid rgba(200,160,74,0.15)", color: on ? gold : dim, fontWeight: on ? 600 : 400, fontFamily: "'DM Sans',sans-serif" });
const btn = (primary) => ({ padding: "8px 16px", fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", border: primary ? "none" : "1px solid rgba(200,160,74,0.3)", background: primary ? "linear-gradient(135deg,#c8a04a,#8b5e1a)" : "transparent", color: primary ? "#1a1008" : gold });
const NA = "Not available";

export function OrderFilterPanel({ open, setOpen, draft, setDraft, onApply, onClear, onRange, appliedRange, methods, matching, total, active }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // quick ranges apply at once; "Select Date" opens the date pickers
  const pickRange = (k) => {
    if (k === "custom") { setDraft((d) => ({ ...d, range: "custom" })); setOpen(true); return; }
    onRange ? onRange(k) : setDraft((d) => ({ ...d, range: k, from: "", to: "" }));
  };
  const current = appliedRange || draft.range;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {RANGES.map(([k, l]) => (
          <span key={k} style={pill(current === k || (k === "custom" && open && draft.range === "custom"))} onClick={() => pickRange(k)}>
            {k === "custom" ? "📅 " : ""}{l}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => setOpen(!open)} style={btn(false)}>🔍 Filter Orders {active ? "•" : ""} {open ? "▲" : "▼"}</button>
        <span style={{ fontSize: 12, color: active ? gold : dim, fontFamily: "'DM Sans',sans-serif" }}>
          {matching} matching order{matching !== 1 ? "s" : ""}{active ? ` of ${total}` : ""}
        </span>
        {active && <button onClick={onClear} style={{ ...btn(false), padding: "4px 10px", fontSize: 11 }}>✕ Clear Filters</button>}
      </div>
      {open && (
        <div style={{ marginTop: 12, padding: 16, background: "#1a1008", border: "1px solid rgba(200,160,74,0.15)", borderRadius: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
            <div><label style={lbl}>Start date</label>
              <input type="date" value={draft.from} onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value, range: "custom" }))} style={inp} /></div>
            <div><label style={lbl}>End date</label>
              <input type="date" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value, range: "custom" }))} style={inp} /></div>
            <div><label style={lbl}>Order status</label>
              <select value={draft.status} onChange={(e) => set("status", e.target.value)} style={inp}>{ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
            <div><label style={lbl}>Payment status</label>
              <select value={draft.pay} onChange={(e) => set("pay", e.target.value)} style={inp}>{PAY_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
            <div><label style={lbl}>Payment method</label>
              <select value={draft.method} onChange={(e) => set("method", e.target.value)} style={inp}>
                <option>All</option>{methods.map((m) => <option key={m}>{m}</option>)}
              </select></div>
            <div style={{ gridColumn: "1/-1" }}><label style={lbl}>Search</label>
              <input value={draft.q} placeholder="Customer name, email, phone, order ID or payment ID" onChange={(e) => set("q", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onApply()} style={inp} /></div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={onApply} style={btn(true)}>Apply Filters</button>
            <button onClick={onClear} style={btn(false)}>Clear Filters</button>
          </div>
          <div style={{ fontSize: 10, color: "#5a3a10", marginTop: 8, fontFamily: "'DM Sans',sans-serif" }}>
            Dates use the order's creation time in your time zone. The same filters (except order status) apply to Failed Payments below.
          </div>
        </div>
      )}
    </div>
  );
}

const CAT = {
  failed:        { label: "FAILED",               color: "#f87171", bg: "rgba(198,40,40,0.15)",  border: "rgba(198,40,40,0.45)" },
  pending:       { label: "PENDING",              color: "#f5c842", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)" },
  cancelled:     { label: "CANCELLED",            color: "#a8a29e", bg: "rgba(168,162,158,0.1)", border: "rgba(168,162,158,0.35)" },
  paid_no_order: { label: "PAID · NO ORDER",      color: "#fb923c", bg: "rgba(251,146,60,0.12)", border: "rgba(251,146,60,0.45)" },
  refunded:      { label: "REFUNDED",             color: "#93c5fd", bg: "rgba(147,197,253,0.1)", border: "rgba(147,197,253,0.35)" },
  order:         { label: "ORDER PLACED",         color: "#4ade80", bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.35)" },
};
const CAT_HELP = {
  failed: "Payment failed at the gateway. No order was created.",
  pending: "Checkout started; payment not completed or not captured yet.",
  cancelled: "Customer closed the payment window without paying.",
  paid_no_order: "Money was captured but no order exists — reconcile (Payment alerts / recovery script).",
  refunded: "Payment was refunded.",
  order: "Payment succeeded and the order is in the list above.",
};
const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : NA);
const val = (v) => (v === undefined || v === null || v === "" ? NA : v);

export function PaymentAttemptsSection({ api, pass, filters, getImgUrl, onMethods }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cat, setCat] = useState("problems");           // problems = everything except successful orders
  const [syncMsg, setSyncMsg] = useState("");
  const { from, to } = dateRange(filters);
  const fromIso = from ? from.toISOString() : "", toIso = to ? to.toISOString() : "";

  const load = async () => {
    setLoading(true); setError("");
    try {
      const qs = new URLSearchParams({ password: pass, ...(fromIso ? { from: fromIso } : {}), ...(toIso ? { to: toIso } : {}) });
      const r = await fetch(`${api}/api/admin/payment-attempts?${qs}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setRows(data.attempts || []);
      onMethods && onMethods([...new Set((data.attempts || []).map(attemptMethod))]);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { if (pass) load(); }, [pass, fromIso, toIso]);

  const syncNow = async () => {
    setSyncMsg("Checking with Razorpay…");
    try {
      const r = await fetch(`${api}/api/admin/payment-attempts/sync`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: pass, days: 7 }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setSyncMsg(`Checked ${d.checked} checkout(s) with Razorpay${d.errors ? `, ${d.errors} could not be checked` : ""}.`);
      load();
    } catch (e) { setSyncMsg("Could not check: " + e.message); }
  };

  const filtered = applyAttemptFilters(rows, filters);
  const counts = filtered.reduce((c, r) => ((c[r.category] = (c[r.category] || 0) + 1), c), {});
  const shown = filtered.filter((r) => (cat === "problems" ? r.category !== "order" : r.category === cat));
  const problems = filtered.length - (counts.order || 0);

  return (
    <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(200,160,74,0.15)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 17, color: text }}>Failed Payments / Incomplete Checkouts</div>
          <div style={{ fontSize: 11, color: dim, fontFamily: "'DM Sans',sans-serif", marginTop: 2 }}>
            Not orders. Nothing here is shipped or counted as revenue. {problems} record{problems !== 1 ? "s" : ""} match the filters.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={btn(false)}>↻ Refresh</button>
          <button onClick={syncNow} title="Ask Razorpay for the result of recent checkouts that have none yet" style={btn(false)}>Check with Razorpay</button>
        </div>
      </div>
      {syncMsg && <div style={{ fontSize: 11, color: gold, marginBottom: 10, fontFamily: "'DM Sans',sans-serif" }}>{syncMsg}</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        <span style={pill(cat === "problems")} onClick={() => setCat("problems")}>All without order ({problems})</span>
        {["failed", "pending", "cancelled", "paid_no_order", "refunded", "order"].map((k) => (
          <span key={k} title={CAT_HELP[k]} style={pill(cat === k)} onClick={() => setCat(k)}>{CAT[k].label} ({counts[k] || 0})</span>
        ))}
      </div>
      {error && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 10 }}>Could not load: {error}</div>}
      {loading ? <div style={{ color: dim, fontSize: 12, padding: "20px 0" }}>Loading…</div>
        : shown.length === 0 ? <div style={{ color: "#5a3a10", fontSize: 12, padding: "24px 0", textAlign: "center", fontFamily: "'DM Sans',sans-serif" }}>No records for this selection.</div>
        : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {shown.map((a) => <AttemptCard key={a.id} a={a} getImgUrl={getImgUrl} />)}
          </div>
        )}
    </div>
  );
}

export function AttemptCard({ a, getImgUrl }) {
  const c = CAT[a.category] || CAT.pending;
  const addr = a.address;
  const addrText = addr ? [addr.fullName || addr.name, addr.houseNo, addr.street, addr.landmark, addr.village || addr.city, addr.district, addr.state, addr.pincode].filter(Boolean).join(", ") : NA;
  const err = a.error && (a.error.description || a.error.reason || a.error.code);
  const row = (k, v, mono) => (
    <div style={{ display: "flex", gap: 8, fontSize: 12, lineHeight: 1.6 }}>
      <span style={{ color: dim, minWidth: 110, flexShrink: 0 }}>{k}</span>
      <span style={{ color: v === NA ? "#5a3a10" : text, fontFamily: mono ? "monospace" : "'DM Sans',sans-serif", wordBreak: "break-all", fontStyle: v === NA ? "italic" : "normal" }}>{v}</span>
    </div>
  );
  return (
    <div style={{ background: "#1a1008", border: `1px solid ${c.border}`, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "12px 18px", background: c.bg }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", color: c.color, border: `1px solid ${c.border}`, padding: "3px 10px", borderRadius: 999 }}>{c.label}</span>
          <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, color: text }}>{a.customer?.name || NA}</span>
          <span style={{ fontSize: 11, color: dim }}>{fmtDT(a.lastAttemptAt || a.createdAt)}</span>
        </div>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: gold }}>₹{Number(a.amount || 0).toLocaleString("en-IN")}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16, padding: "14px 18px" }}>
        <div>
          {row("Email", val(a.customer?.email))}
          {row("Phone", val(a.customer?.phone))}
          {row("Address", addrText)}
          {row("Checkout ID", val(a.id), true)}
          {a.orderId ? row("Order", `#${String(a.orderId).slice(-6).toUpperCase()}`, true) : null}
        </div>
        <div>
          {row("Gateway", val(a.gateway && a.gateway[0].toUpperCase() + a.gateway.slice(1)))}
          {row("Method", a.method ? attemptMethod(a) + (a.vpa ? ` (${a.vpa})` : "") : NA)}
          {row("Status", String(a.paymentStatus || "").toUpperCase() || NA)}
          {row("Failure reason", err ? `${a.error.description || ""}${a.error.reason ? ` [${a.error.reason}]` : ""}${a.error.code ? ` (${a.error.code})` : ""}` : (a.category === "failed" ? NA : "—"))}
          {row("Payment ID", a.paymentIds?.length ? a.paymentIds.join(", ") : NA, true)}
          {row("Gateway ref.", a.gatewayRefs?.length ? a.gatewayRefs.join(", ") : NA, true)}
          {a.cancelledAt ? row("Window closed", fmtDT(a.cancelledAt)) : null}
        </div>
      </div>
      <div style={{ padding: "0 18px 14px" }}>
        <div style={{ fontSize: 10, color: "#5a3a10", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Products ({a.items?.length || 0})</div>
        {!a.items?.length ? <div style={{ fontSize: 12, color: "#5a3a10", fontStyle: "italic" }}>Not available (cart was not saved for this checkout)</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {a.items.map((it, j) => {
              const img = it.image ? getImgUrl(it.image) : null;
              return (
                <div key={j} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {img ? <img src={img} alt={it.name} onError={(e) => { e.target.style.display = "none"; }} style={{ width: 44, height: 52, objectFit: "cover", borderRadius: 4, border: "1px solid rgba(200,160,74,0.2)" }} /> : null}
                  <div style={{ fontSize: 12, color: text, fontFamily: "'DM Sans',sans-serif" }}>
                    {it.name || NA}
                    <div style={{ fontSize: 11, color: dim }}>Size: {it.size || NA} · Colour: {it.color || NA} · Qty: {it.quantity} · ₹{Number(it.price || 0).toLocaleString("en-IN")}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {a.attempts?.length > 1 && (
          <div style={{ marginTop: 10, fontSize: 11, color: dim, fontFamily: "'DM Sans',sans-serif" }}>
            {a.attempts.length} payment attempts: {a.attempts.map((t) => `${t.status}${t.error ? ` (${t.error})` : ""}`).join(" → ")}
          </div>
        )}
      </div>
    </div>
  );
}